import React, { useContext, useEffect, useState } from 'react'
import { Document, Page, Text, View, Image, StyleSheet, pdf } from '@react-pdf/renderer'
import api from '../services/api'
import { MainContext } from '../context/MainContext'

const B = 0.5
const BC = '#000'

const styles = StyleSheet.create({
  page: {
    paddingBottom: 40,
    paddingTop: 19,
    paddingLeft: 20,
    paddingRight: 20,
    fontSize: 8,
    flexDirection: 'column',
  },
  header: {
    alignItems: 'center',
    marginBottom: 10,
  },
  headerLogo: {
    width: 50,
    height: 50,
    marginBottom: 4,
  },
  headerText: {
    textAlign: 'center',
    width: '100%',
  },
  clubName: {
    fontWeight: 'bold',
    fontSize: 12,
    marginBottom: 2,
    textAlign: 'center',
  },
  clubInfo: {
    fontSize: 8,
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 11,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold',
  },
  // célula base: borda direita + baixo sempre presentes
  cell: {
    padding: 5,
    borderRightWidth: B,
    borderRightColor: BC,
    borderBottomWidth: B,
    borderBottomColor: BC,
    borderTopWidth: B,
    borderTopColor: BC,
  },
  // primeira célula de cada linha adiciona borda esquerda
  cellFirst: {
    borderLeftWidth: B,
    borderLeftColor: BC,
  },
  // primeira linha (header/título) adiciona borda topo
  cellTop: {
    borderTopWidth: B,
    borderTopColor: BC,
  },
})

interface EventReport {
  eventId: number
  eventTitle: string
  stages: { id: number; title: string }[]
  categories: { categoryName: string; min_score: number; max_score: number; athletes: any[] }[]
}

interface Props {
  title: string
  eventIds?: number[]
  preloadedReports?: EventReport[]
  childToParent?: () => void
}

const ConsolidatedReportPrinting: React.FC<Props> = ({ title, eventIds, preloadedReports, childToParent }) => {
  const { club, setLoading } = useContext(MainContext)
  const [reports, setReports] = useState<EventReport[]>([])
  const [dataLoaded, setDataLoaded] = useState(false)

  const formatScoreRange = (min: number, max: number): string => {
    if (min && max) return ` (${min}-${max})`
    if (max) return ` (>= ${max})`
    if (min) return ` (<= ${min})`
    return ''
  }

  async function loadData() {
    if (preloadedReports) {
      setReports(preloadedReports)
      return
    }
    const loaded: EventReport[] = []
    for (const eventId of eventIds ?? []) {
      const [eventRes, reportRes] = await Promise.all([
        api.get(`/api/events/${eventId}`),
        api.get(`/api/event_results/consolidated-report/${eventId}`),
      ])
      if (reportRes.data.categories?.length > 0) {
        loaded.push({
          eventId,
          eventTitle: eventRes.data.title,
          stages: reportRes.data.stages,
          categories: reportRes.data.categories,
        })
      }
    }
    setReports(loaded)
  }

  const handleOpenPDF = async () => {
    const blob = await pdf(<ReportPDF />).toBlob()
    const url = URL.createObjectURL(blob)
    window.open(url)
  }

  useEffect(() => {
    setLoading(true)
    loadData().then(() => setDataLoaded(true))
  }, [])

  useEffect(() => {
    if (dataLoaded) {
      handleOpenPDF().then(() => {
        setLoading(false)
        if (childToParent) childToParent()
      })
    }
  }, [dataLoaded])

  const ReportPDF = () => (
    <Document>
      {reports.map((report) => {
        const stageWidth = report.stages.length > 0 ? `${40 / report.stages.length}%` : '10%'

        return (
          <Page
            key={report.eventId}
            style={styles.page}
            size="A4"
            orientation={report.stages.length > 5 ? 'landscape' : 'portrait'}
          >
            <View style={styles.header}>
              <Image style={styles.headerLogo} src="/logo192.png" />
              <View style={styles.headerText}>
                <Text style={styles.clubName}>{club?.fantasia}</Text>
                <Text style={styles.clubInfo}>CNPJ: {club?.cnpj}</Text>
                <Text style={styles.clubInfo}>
                  {club?.address?.street}, {club?.address?.number}
                  {club?.address?.complement ? `, ${club?.address?.complement}` : ''} -{' '}
                  {club?.address?.city?.name} / {club?.address?.city?.state?.abbreviation} | Tel:{' '}
                  {club?.phone}
                </Text>
              </View>
            </View>

            <Text style={styles.subtitle}>{title} {reports.length > 1 && <> - {report.eventTitle}</>}</Text>

            {report.categories.map((category, idx) => (
              <View key={idx}>
                <View style={[styles.tableRow, { marginTop: 10 }]} wrap={false}>
                  <Text style={[styles.cell, styles.cellFirst, styles.cellTop, { width: '50%' }]}>
                    Ranking {report.eventTitle} - Cat {category.categoryName}
                    {formatScoreRange(category.min_score, category.max_score)}
                  </Text>
                  <Text style={[styles.cell, styles.cellTop, { width: '50%', textAlign: "center" }]}>
                    Etapas
                  </Text>
                </View>

                <View style={[styles.tableRow, styles.tableHeader]} wrap={false}>
                  <Text style={[styles.cell, styles.cellFirst, styles.cellTop, { width: '10%', textAlign: 'center' }]}>Pos.</Text>
                  <Text style={[styles.cell, styles.cellTop, { width: '40%' }]}>Atleta</Text>
                  {report.stages.map((stage) => (
                    <Text key={stage.id} style={[styles.cell, styles.cellTop, { width: stageWidth, textAlign: 'center' }]}>
                      {stage.title}
                    </Text>
                  ))}
                  <Text style={[styles.cell, styles.cellTop, { width: '10%', textAlign: 'center' }]}>Total</Text>
                </View>

                {category.athletes.map((athlete: any, index: number) => (
                  <View key={athlete.athlete_id} style={styles.tableRow} wrap={false}>
                    <Text style={[styles.cell, styles.cellFirst, { width: '10%', textAlign: 'center' }]}>
                      {index + 1}º
                    </Text>
                    <Text style={[styles.cell, { width: '40%' }]}>{athlete.full_name}</Text>
                    {athlete.results.map((res: any, i: number) => (
                      <Text key={i} style={[styles.cell, { width: stageWidth, textAlign: 'center', color: res.discarded ? '#aaa' : '#000', textDecoration: res.discarded ? 'line-through' : 'none' }]}>
                        {res.value > 0 ? res.value : '-'}
                      </Text>
                    ))}
                    <Text style={[styles.cell, { width: '10%', textAlign: 'center' }]}>
                      {athlete.total}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
          </Page>
        )
      })}
    </Document>
  )

  return null
}

export default ConsolidatedReportPrinting
